'use client';

import { Autocomplete, TextPassage } from '@petsmart-ui/sparky';
import { useState } from 'react';

import {
    CUPS_SUGGESTIONS,
    CUPS_UNIT_SUFFIX,
    formatCupsAmount,
    formatCupsWithUnit,
    isInvalidNonEmptyTotalCupsPerDay,
} from '../model';

const HELPER_TEXT = 'Combined amount for all pets eating this food. Max 20 cups.';

const stripUnitSuffix = (text: string) => text.replace(new RegExp(`${CUPS_UNIT_SUFFIX}$`, 'i'), '').trim();

type TotalCupsPerDayFieldProps = {
    value: string;
    errorMessage?: string;
    onValueChange: (value: string) => void;
    onFieldBlur: () => void;
};

export const TotalCupsPerDayField = ({
    value,
    errorMessage,
    onValueChange,
    onFieldBlur,
}: TotalCupsPerDayFieldProps) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const amount = formatCupsAmount(value);
    const shouldAppendCupsSuffix = Boolean(value) && !isFocused && !isInvalidNonEmptyTotalCupsPerDay(amount);
    const displayValue = shouldAppendCupsSuffix ? formatCupsWithUnit(value) : value;
    const activeSearchTerm = isTyping ? searchTerm : isFocused ? value : '';
    const visibleSuggestions = activeSearchTerm
        ? CUPS_SUGGESTIONS.filter(option => option.toLowerCase().startsWith(activeSearchTerm.toLowerCase()))
        : [];
    const hasSuggestions = visibleSuggestions.length > 0;

    const selectOption = (option: string) => {
        const selectedValue = stripUnitSuffix(option);
        setSearchTerm(selectedValue);
        setIsTyping(false);
        onValueChange(selectedValue);
        setIsClosing(true);
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLElement) activeElement.blur();
    };

    return (
        <Autocomplete
            label="Total cups per day"
            className={`frequency-calculator-drawer__field${isClosing ? ' is-closing' : ''}${
                hasSuggestions ? '' : ' has-no-suggestions'
            }`}
            data-testid="frequency-calculator-cups-input"
            value={displayValue}
            custom
            revealOnFocus={hasSuggestions}
            isError={Boolean(errorMessage)}
            fieldNote={errorMessage ?? HELPER_TEXT}
            clearCustomValuesOnBlur={false}
            onFocus={() => {
                setIsFocused(true);
                setIsClosing(false);
            }}
            onBlur={() => {
                setIsFocused(false);
                setIsTyping(false);
                const normalizedValue = formatCupsAmount(value);
                if (normalizedValue !== value) {
                    onValueChange(normalizedValue);
                }
                onFieldBlur();
            }}
            onInternalValueChange={text => {
                const nextValue = stripUnitSuffix(text);
                setSearchTerm(nextValue);
                setIsTyping(Boolean(nextValue));
                onValueChange(nextValue);
            }}
        >
            {visibleSuggestions.map(option => (
                <TextPassage
                    key={option}
                    className="sparky-c-autocomplete__options"
                    onMouseDown={event => {
                        event.preventDefault();
                        selectOption(option);
                    }}
                >
                    {option}
                </TextPassage>
            ))}
        </Autocomplete>
    );
};
